import { t } from "@lingui/macro";
import { useAtomValue, useSetAtom } from "jotai";

import { PatronsListItem } from "@/features/AddPatrons/components/PatronsListItem";
import type { Patron } from "@/features/AddPatrons/types";
import {
  Box,
  Divider,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { enterEditableRoundsPrivateStepAtom } from "../../atoms/actions/EnterEditableRoundsPrivateStepAtom/enterEditableRoundsPrivateStepAtom";
import {
  canSwapPlayersBetweenGroups,
  swapPlayersBetweenGroupsAtom,
} from "../../atoms/actions/swapPlayersBetweenGroupsAtom/swapPlayersBetweenGroupsAtom";
import { currentBookingAtom } from "../../atoms/stores";
import { PatronEditBooking } from "../../types";

const patronTypeByClientType: Record<
  PatronEditBooking.RoundPlayer["clientType"],
  Patron["type"]
> = {
  member: "Member",
  memberHold: "Member Hold",
  customer: "Customer",
  unknownCustomer: "Customer",
  guest: "Guest",
  newGuest: "New Guest",
  unknownGuest: "Unknown Guest",
};

const playerName = (player: PatronEditBooking.RoundPlayer): string => {
  if (player.firstName || player.lastName) {
    return `${player.firstName ?? ""} ${player.lastName ?? ""}`.trim();
  }
  switch (player.clientType) {
    case "memberHold":
      return t`Member Hold`;
    case "unknownGuest":
    case "newGuest":
      return t`New Guest`;
    case "unknownCustomer":
      return t`New Player`;
    default:
      return t`Player`;
  }
};

type Props = {
  roundId: string;
  playerType: "member" | "guest";
  search: string;
};

export const SwapPlayerCandidates = ({
  roundId,
  playerType,
  search,
}: Props) => {
  const booking = useAtomValue(currentBookingAtom);
  const swapPlayers = useSetAtom(swapPlayersBetweenGroupsAtom);
  const returnToEditableRounds = useSetAtom(enterEditableRoundsPrivateStepAtom);
  const normalizedSearch = search.trim().toLowerCase();
  const candidates = (booking?.rounds ?? [])
    .filter((round) => {
      const isGuest = ["guest", "newGuest", "unknownGuest"].includes(
        round.player.clientType
      );
      return (
        isGuest === (playerType === "guest") &&
        canSwapPlayersBetweenGroups(
          booking?.rounds ?? [],
          roundId,
          round.localId,
          booking?.groupCapacity
        ) &&
        playerName(round.player).toLowerCase().includes(normalizedSearch)
      );
    })
    .sort((a, b) => a.index - b.index);

  return (
    <VStack>
      {candidates.map((round) => {
        const groupNumber = (round.bookingIndex ?? 0) + 1;
        const name = playerName(round.player);
        return (
          <PatronsListItem
            key={round.localId}
            testID={`swap-player-candidate-${round.localId}`}
            item={{
              playerId: round.player.id,
              internalIdentifier: round.localId,
              firstName: round.player.firstName,
              lastName: round.player.lastName,
              profileImageId: round.player.profileImagePublicId,
              type: patronTypeByClientType[round.player.clientType],
              memberGroups: [],
            }}
            displayName={name}
            description={t`Group ${groupNumber}`}
            selectionAction="swap"
            selectionAccessibilityLabel={t`Swap with ${name} in Group ${groupNumber}`}
            handleCheck={() => {
              swapPlayers({
                sourceRoundId: roundId,
                targetRoundId: round.localId,
              });
              returnToEditableRounds();
            }}
          />
        );
      })}
      {candidates.length > 0 ? (
        <Box px={6} py={2}>
          <Divider testID={`swap-player-candidates-divider-${playerType}`} />
        </Box>
      ) : null}
    </VStack>
  );
};
